<template>
  <div
    class="inline-block max-w-full space-y-2"
    :style="imageDisplayWidth ? { width: `${imageDisplayWidth}px` } : undefined"
  >
    <div
      v-if="url"
      class="max-w-full overflow-hidden rounded-lg shadow-sm"
      :class="
        isTallImage ? 'h-[clamp(220px,48vh,360px)] w-[min(65vw,280px)]' : 'w-fit'
      "
    >
      <img
        ref="imageElement"
        :src="url"
        :alt="fileName || 'Imagen'"
        class="block cursor-pointer transition-opacity hover:opacity-90"
        :class="
          isTallImage
            ? 'h-full w-full object-cover'
            : 'h-auto w-auto max-h-[min(48vh,360px)] max-w-[min(65vw,380px)] object-contain'
        "
        loading="lazy"
        @load="handleImageLoad"
        @click="showMediaViewer = true"
      />
      <MediaViewer
        v-model:visible="showMediaViewer"
        :items="mediaItems"
        :initial-item-id="messageId"
      />
    </div>
    <div
      v-else
      class="flex items-center justify-center p-8 rounded bg-opacity-10"
      :class="isSent ? 'bg-white' : 'bg-black'"
    >
      <ImageIcon
        :size="48"
        class="opacity-50"
        :class="
          isSent
            ? 'text-[var(--p-primary-900)] dark:text-[var(--p-primary-100)]'
            : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
        "
      />
    </div>
    <div
      v-if="caption"
      class="w-full min-w-0 break-words text-[13px]"
      :class="
        isSent
          ? 'text-[var(--p-surface-0)] dark:text-[var(--p-primary-50)]'
          : 'text-[var(--p-primary-950)] dark:text-[var(--p-primary-100)]'
      "
    >
      <FormattedText :text="caption" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { ImageIcon } from 'lucide-vue-next'
import MediaViewer from '../media/MediaViewer.vue'
import { chatMediaItemsKey, type ChatMediaItem } from '../../composables/useChatMediaViewer'
import FormattedText from '../../atoms/text/FormattedText.vue'

interface Props {
  url?: string
  messageId?: string
  fileName?: string
  caption?: string
  isSent: boolean
}

const props = defineProps<Props>()

const showMediaViewer = ref(false)
const isTallImage = ref(false)
const imageElement = ref<HTMLImageElement>()
const imageDisplayWidth = ref<number>()
let imageResizeObserver: ResizeObserver | undefined

const syncImageDisplayWidth = () => {
  const width = imageElement.value?.getBoundingClientRect().width ?? 0
  imageDisplayWidth.value = width > 0 ? Math.ceil(width) : undefined
}

const observeImageWidth = () => {
  imageResizeObserver?.disconnect()
  if (!imageElement.value || typeof ResizeObserver === 'undefined') return

  imageResizeObserver = new ResizeObserver(syncImageDisplayWidth)
  imageResizeObserver.observe(imageElement.value)
}

const handleImageLoad = (event: Event) => {
  const image = event.target as HTMLImageElement
  isTallImage.value = image.naturalHeight / image.naturalWidth >= 1.35
  nextTick(() => {
    syncImageDisplayWidth()
    observeImageWidth()
  })
}

watch(
  () => props.url,
  () => {
    isTallImage.value = false
    imageDisplayWidth.value = undefined
  },
)

onBeforeUnmount(() => imageResizeObserver?.disconnect())

const injectedMediaItems = inject(chatMediaItemsKey, null)
const fallbackMediaItems = computed<ChatMediaItem[]>(() =>
  props.url
    ? [
        {
          id: props.messageId || props.url,
          type: 'image',
          url: props.url,
          fileName: props.fileName,
          caption: props.caption,
        },
      ]
    : [],
)
const mediaItems = computed(() => injectedMediaItems?.value ?? fallbackMediaItems.value)
</script>
